// src/pages/Profile.js
import React, { useState, useEffect } from 'react';
import {  Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import ConfirmationModal from '../components/ConfirmationModal';

const Profile = () => {
  const token = localStorage.getItem('token');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updateSuccess, setUpdateSuccess] = useState('');

  // Form fields for updating profile details
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');

  // State for editing a submission.
  const [editingPost, setEditingPost] = useState(null);
  const [editImageURL, setEditImageURL] = useState('');
  const [editStory, setEditStory] = useState('');
  const [editHashtags, setEditHashtags] = useState('');

  // State for deleting a submission.
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Fetch the profile information on component mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/user/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.error || 'Failed to fetch profile');
        }
        const data = await response.json();
        setProfile(data);
        setUsername(data.user.username);
        setEmail(data.user.email);
      } catch (err) {
        setError(err.message);
      }
      setLoading(false);
    };
    fetchProfile();
  }, [token]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setUpdateSuccess('');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/user/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ username, email }),
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to update profile');
      }
      const data = await response.json();
      setUpdateSuccess('Profile updated successfully!');
      setProfile((prev) => ({ ...prev, user: data.user }));
      // Also update the stored user info in localStorage
      localStorage.setItem('user', JSON.stringify(data.user));
    } catch (err) {
      setError(err.message);
    }
  };

  // Start editing a submission.
  const startEditing = (post) => {
    setEditingPost(post);
    setEditImageURL(post.image_url);
    setEditStory(post.story);
    setEditHashtags(post.hashtags);
  };

  // Handle submission edit.
  const handleEditSubmit = async (e, postId) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/dogs/${postId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          image_url: editImageURL,
          story: editStory,
          hashtags: editHashtags,
        }),
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Error updating submission');
      }
      const data = await response.json();
      // Update the submissions list.
      setProfile((prev) => ({
        ...prev,
        submissions: prev.submissions.map((post) =>
          post.id === postId ? data.dog : post
        ),
      }));
      setEditingPost(null);
    } catch (err) {
      toast.error(err.message);
    }
  };

// New function to initiate deletion
  const confirmDelete = (postId) => {
    setDeleteTargetId(postId);
    setShowConfirmModal(true);
  };
  
  // Updated deletion function to be called on confirmation:
  const handleDeleteConfirmed = async () => {
    if (!deleteTargetId) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/dogs/${deleteTargetId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Error deleting submission');
      }
      // Remove the submission from state.
      setProfile((prev) => ({
        ...prev,
        submissions: prev.submissions.filter((post) => post.id !== deleteTargetId),
      }));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setShowConfirmModal(false);
      setDeleteTargetId(null);
    }
  };
  

  const handleUnlike = async (dogId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/dogs/${dogId}/heart?user_id=${profile.user.id}`,
        { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        // Remove the post from liked posts.
        setProfile((prev) => ({
          ...prev,
          liked: prev.liked.filter((post) => post.id !== dogId),
        }));
      } else {
        const errData = await response.json();
        toast.error(errData.error || "Error unliking post");
      }
    } catch (err) {
      console.error("Error unliking post:", err);
      toast.error("Error unliking post");
    }
  };

  if (loading) {
    return <div className="container mx-auto p-4">Loading profile...</div>;
  }

  if (error) {
    return <div className="container mx-auto p-4 text-red-500">{error}</div>;
  }

  return (
    <div className="container mx-auto p-4 space-y-8">
      <h1 className="text-4xl font-bold">My Profile</h1>
      
      {/* Profile Details & Update Form */}
      <div className="bg-white p-6 shadow rounded">
        <h2 className="text-2xl font-bold mb-4">Profile Details</h2>
        {updateSuccess && <p className="text-green-500 mb-4">{updateSuccess}</p>}
        <form onSubmit={handleUpdate} className="space-y-4">
          <div>
            <label className="block mb-1 font-medium">Username:</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full border p-2 rounded"
              required
            />
          </div>
          <div>
            <label className="block mb-1 font-medium">Email:</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border p-2 rounded"
              required
            />
          </div>
          <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded">
            Update Profile
          </button>
        </form>
      </div>

      {/* User's Dog Submissions */}
      <div className="bg-white p-6 shadow rounded">
        <h2 className="text-2xl font-bold mb-4">My Submissions</h2>
        {profile.submissions && profile.submissions.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {profile.submissions.map((post) => (
              <div key={post.id} className="border p-4 rounded hover:shadow-lg transition relative">
                {editingPost && editingPost.id === post.id ? (
                  <form onSubmit={(e) => handleEditSubmit(e, post.id)} className="space-y-2">
                    <div>
                      <label className="block text-sm font-medium">Image URL:</label>
                      <input
                        type="url"
                        value={editImageURL}
                        onChange={(e) => setEditImageURL(e.target.value)}
                        className="w-full border p-2 rounded"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium">Story:</label>
                      <textarea
                        value={editStory}
                        onChange={(e) => setEditStory(e.target.value)}
                        className="w-full border p-2 rounded"
                        required
                        maxLength="500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium">Hashtags:</label>
                      <input
                        type="text"
                        value={editHashtags}
                        onChange={(e) => setEditHashtags(e.target.value)}
                        className="w-full border p-2 rounded"
                        required
                      />
                    </div>
                    <div className="flex space-x-2">
                      <button type="submit" className="bg-blue-500 text-white px-3 py-1 rounded">
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingPost(null)}
                        className="bg-gray-500 text-white px-3 py-1 rounded"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <>
                    {post.image_url && (
                      <img src={post.image_url} alt="Dog" className="w-full h-48 object-cover rounded mb-2" />
                    )}
                    <p className="mb-2">{post.story}</p>
                    <p className="text-sm text-gray-500 mb-2">Hashtags: {post.hashtags}</p>
                    <p className="text-sm text-gray-500 mb-2">Hearts: {post.hearts}</p>
                    <div className="mt-2 flex space-x-2">
                      <button
                        onClick={() => startEditing(post)}
                        className="bg-green-500 text-white px-3 py-1 rounded"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => confirmDelete(post.id)}
                        className="bg-red-500 text-white px-3 py-1 rounded"
                      >
                        Delete
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center">
            <p>You have not submitted any dog posts yet.</p>
            <Link to="/submit" className="text-blue-500 hover:underline font-medium">
              Click here to submit your dog post!
            </Link>
          </div>
        )}
      </div>

      {/* User's Liked Posts */}
      <div className="bg-white p-6 shadow rounded">
        <h2 className="text-2xl font-bold mb-4">Liked Posts</h2>
        {profile.liked && profile.liked.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {profile.liked.map((post) => (
              <div key={post.id} className="border p-4 rounded hover:shadow-lg transition relative">
                {post.image_url && (
                  <img src={post.image_url} alt="Dog" className="w-full h-48 object-cover rounded mb-2" />
                )}
                <p className="mb-2">{post.story}</p>
                <p className="text-sm text-gray-500 mb-2">Hashtags: {post.hashtags}</p>
                <p className="text-sm text-gray-500">Hearts: {post.hearts}</p>
                <button
                  onClick={() => handleUnlike(post.id)}
                  className="absolute top-2 right-2 bg-gray-200 hover:bg-gray-300 text-gray-700 px-2 py-1 rounded"
                >
                  Unlike
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center">
            <p>You have not liked any posts yet.</p>
            <Link to="/" className="text-blue-500 hover:underline font-medium">
              Click here to view and like posts!
            </Link>
          </div>
        )}
      </div>
      {showConfirmModal && (
        <ConfirmationModal
            message="Are you sure you want to delete this submission?"
            onConfirm={handleDeleteConfirmed}
            onCancel={() => {
            setShowConfirmModal(false);
            setDeleteTargetId(null);
            }}
        />
        )}
    </div>
  );
};

export default Profile;

